import { PageContainer } from "src/components/organisms/contents";
import { ReactComponent as IconRightArrow } from "../../../styles/assets/svg/icon_RightChevron.svg";
import { ReactComponent as IconDownArrow } from "../../../styles/assets/svg/icon_down_arrow.svg";
import { ReactComponent as IconBell } from "../../../styles/assets/svg/icon_yellow_bell.svg";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { confirmDialog } from "src/components/containers/ConfirmDialog";
import { ChoicePillInfo } from "./ChoicePillInfo";
import { ContentsBox } from "src/pages/diet/components/DietWritePage";
import { daysOfWeek, PillInfo } from "./PillInfo";
import {
  useCreateMedication,
  useUpdateMedication,
} from "src/common/hooks/usePillRegister";
import { useImgUpload } from "src/common/hooks/useImgRegister";
import { SubConHeader } from "src/components/atoms/SubHeaderBox/SubConHeader";
import { FadeLoading } from "src/components/atoms/Spinners/FadeLoading";
import { ToggleBtn } from "src/components/atoms/ToggleButton/ToggleBtn";

export const PillWritePage = () => {
  const navi = useNavigate();
  const { state } = useLocation(); // selectedDiet oid 가져오기
  const [showChoice, setShowChoice] = useState<boolean>(false); // 선택 입력사항 모달 상태
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isAlert, setIsAlert] = useState<boolean>(false);

  const [medName, setMedName] = useState<string>(""); // 매일/요일선택 모드
  const [mode, setMode] = useState<"daily" | "weekly">("daily"); // 매일/요일선택 모드
  const [selectedDays, setSelectedDays] = useState<string[]>([]); // 요일 선택
  const [eatCount, setEatCount] = useState<number>(1); // 하루 복용 횟수
  const [timers, setTimers] = useState<string[]>(["08:00:00"]); // 복용 시간
  const [activeTimerIndex, setActiveTimerIndex] = useState<number | null>(null); // 현재 열려 있는 시간 모달 index
  const [imgFileUrl, setImgFileUrl] = useState<any>(""); // 약 사진
  const [count, setCount] = useState<number>(1); // 1회 복용량
  const [isOneDay, setIsOneDay] = useState<string | null>(null); // 1회 복용량
  const [isPillPoint, setIsPillPoint] = useState<string | null>(null); // 복용 시점
  const [memo, setMemo] = useState<string>(""); // 메모

  /* 복약 기록 추가하기 */
  const { mutateAsync: createMedication } = useCreateMedication();

  /* 복약 기록 수정하기 */
  const { mutateAsync: updateMedication } = useUpdateMedication({
    oid: state?.item?.oid,
  });

  /* 복약 삭제 하기 */
  const { mutateAsync: deleteMedication } = useUpdateMedication({
    oid: state?.item?.oid,
  });

  /* 이미지 업로드 */
  const { mutateAsync: uploadFile } = useImgUpload();

  useEffect(() => {
    setTimers((prev) => {
      if (prev.length < eatCount) {
        // 부족하면 빈칸 추가
        return [...prev, ...Array(eatCount - prev.length).fill("08:00:00")];
      } else if (prev.length > eatCount) {
        // 많으면 잘라내기
        return prev.slice(0, eatCount);
      }
      return prev;
    });
  }, [eatCount]);

  useEffect(() => {
    if (!state?.item) return;

    setMedName(state?.item.mediName);
    if (state?.item.daysOfWeek?.length > 6) {
      setMode("daily");
      setSelectedDays(state?.item.daysOfWeek || []);
    } else {
      setMode("weekly");
      setSelectedDays(state?.item.daysOfWeek || []);
    }
    setEatCount(state?.item.timesPerDay);
    setTimers(state?.item.timeList);
    setCount(state?.item.dose || 1);
    setIsOneDay(state?.item.doseUnit);
    setIsPillPoint(state?.item.intakeTiming);
    setMemo(state?.item.memo);
    setIsAlert(state?.item.alarmYn);
    setShowChoice(true);
    if (state?.bellActive?.some((oid: string) => oid === state?.item.oid)) {
      setIsAlert(true);
    }
  }, [state?.item, state?.bellActive]);

  /* 입력 확인 조건 */
  const isAllFilled =
    !!medName && // 약 이름 존재
    !!mode && // 모드 존재
    (mode === "daily" || (mode === "weekly" && selectedDays.length > 0)) && // weekly면 요일 선택 필수
    eatCount > 0 && // 하루 복용 횟수
    timers.length === eatCount && // timers 배열 길이가 eatCount와 동일
    timers.every((t) => t); // timers 안의 값이 모두 존재

  /* 추가하기 */
  const handleSave = async () => {
    let fileOid: string | undefined = undefined;
    setIsLoading(true);

    if (imgFileUrl?.file) {
      const { response } = await uploadFile(imgFileUrl.file);
      setIsLoading(false);
      fileOid = response.oid;
    } else if (state?.item?.fileOid) {
      // 기존 이미지가 있으면 그대로 사용
      fileOid = state.item.fileOid;
    }
    const payload = [
      {
        fileOid,
        mediTitle: "test",
        mediName: medName,
        daysOfWeek:
          mode === "daily"
            ? daysOfWeek.map((d) => d.value) // daily면 월~일까지 전부
            : selectedDays,
        timeList: timers.map((t) => t),
        memo: memo,
        dose: isOneDay === null ? null : count,
        doseUnit: isOneDay,
        timesPerDay: eatCount,
        intakeTiming: isPillPoint,
        delYn: false,
        alarmYn: isAlert,
      },
    ];

    if (state?.item?.oid) {
      await updateMedication(payload);
    } else {
      await createMedication(payload);
    }
  };

  const handleDelete = async () => {
    const payload = {
      oid: state?.item.oid,
      delYn: true,
    };

    const isConfirm = await confirmDialog({
      text: `해당 약을 삭제하시겠습니까?`,
      title: "삭제하기",
      options: {
        trueButtonText: "삭제하기",
        falseButtonText: "돌아가기",
      },
    });
    if (isConfirm) {
      await deleteMedication(payload);
      navi("/pill", {
        replace: true,
        state: { toast: "복약 기록이 삭제되었습니다!", date: state?.date },
      });
    }
  };

  /* 나가기 모달 */
  const handleOutPage = async () => {
    const isConfirm = await confirmDialog({
      text: `지금 나가면 작성한 내용이 저장되지 않아요. \n 계속 작성하시겠어요?`,
      title: "작성 중인 내용이 있어요",
      options: {
        trueButtonText: "계속 작성하기",
        falseButtonText: "작성 취소",
      },
    });
    if (!isConfirm) {
      navi("/pill", { replace: true, state: { date: state?.date } });
    }
  };
  return (
    <PageContainer isNavBar={false} active={null}>
      <>
        {isLoading && <FadeLoading />}
        {/* 헤더 */}
        <SubConHeader
          title="복약 기록"
          onClickArrow={() =>
            isAllFilled
              ? navi("/pill", {
                  replace: true,
                  state: { date: state?.date },
                })
              : handleOutPage()
          }
        />

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            gap: 24,
            paddingBottom: "90px",
          }}
        >
          {/* 상단 내용 */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "flex-start",
              gap: 2,
              width: "100%",
            }}
          >
            <div
              style={{
                color: "var(--neutral-800, #262626)",
                fontSize: 20,
                fontWeight: "700",
                lineHeight: "28px",
              }}
            >
              {state?.item ? "약 수정하기" : "약 추가하기"}
            </div>
            <div
              style={{
                color: "var(--neutral-500, #737373)",
                fontSize: 16,
                fontWeight: "500",
                lineHeight: "24px",
              }}
            >
              약에 대한 정보를 입력해주세요.
            </div>
          </div>
          {/* 약 기본 정보 입력 */}
          <ContentsBox>
            <PillInfo
              medName={medName}
              mode={mode}
              selectedDays={selectedDays}
              eatCount={eatCount}
              timers={timers}
              activeTimerIndex={activeTimerIndex}
              setMedName={setMedName}
              setMode={setMode}
              setSelectedDays={setSelectedDays}
              setEatCount={setEatCount}
              setTimers={setTimers}
              setActiveTimerIndex={setActiveTimerIndex}
            />
          </ContentsBox>

          {/* 선택 입력사항 */}
          <ContentsBox>
            <div
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
              onClick={() => setShowChoice(!showChoice)}
            >
              <p>선택 입력사항</p>
              {showChoice ? (
                <IconDownArrow onClick={() => setShowChoice(false)} />
              ) : (
                <IconRightArrow onClick={() => setShowChoice(true)} />
              )}
            </div>
            {showChoice && (
              <ChoicePillInfo
                item={state?.item}
                count={count}
                isOneDay={isOneDay}
                isPillPoint={isPillPoint}
                setImgFileUrl={setImgFileUrl}
                setCount={setCount}
                setIsOneDay={setIsOneDay}
                setIsPillPoint={setIsPillPoint}
                memo={memo}
                setMemo={setMemo}
              />
            )}
          </ContentsBox>

          {/* 알림받기 */}
          <ContentsBox>
            <div
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <IconBell />
              <p style={{ marginLeft: "10px" }}>알림 받기</p>
              <ToggleBtn isActive={isAlert} setIsActive={setIsAlert} />
            </div>
            <p
              style={{
                color: "#a3a3a3",
                fontSize: "14px",
                fontWeight: 500,
                lineHeight: "20px",
              }}
            >
              복용 시간에 맞춰 알림 드릴께요!
            </p>
          </ContentsBox>

          {/* 하단 버튼 */}
          {state?.item?.oid ? (
            <div
              style={{
                width: "100%",
                maxWidth: "45rem",
                padding: 16,
                bottom: 0,
                position: "fixed",
                // margin: "0 -16px",
                background: "white",
                borderTop: "1px rgba(0, 0, 0, 0.05) solid",
                display: "flex",
                // flexDirection: "column",
                gap: 12,
                boxSizing: "border-box",
                left: "50%",
                transform: "translateX(-50%)",
              }}
            >
              <button
                style={{
                  width: "100%",
                  height: 48,
                  background: "#F5F5F5",
                  borderRadius: 8,
                  border: "none",
                  cursor: "pointer",
                  fontSize: 16,
                  fontWeight: 500,
                  color: "#FF5253",
                }}
                onClick={handleDelete}
              >
                삭제하기
              </button>
              <button
                style={{
                  width: "100%",
                  height: 48,
                  background: isAllFilled ? "#F57C00" : "#FFD9B3",
                  borderRadius: 8,
                  border: "none",
                  cursor: isAllFilled ? "pointer" : "",
                  fontSize: 16,
                  fontWeight: 500,
                  color: "white",
                }}
                disabled={!isAllFilled}
                onClick={handleSave}
              >
                수정완료
              </button>
            </div>
          ) : (
            <div
              style={{
                width: "100%",
                maxWidth: "45rem",
                padding: 16,
                bottom: 0,
                position: "fixed",
                // margin: "0 -16px",
                background: "white",
                borderTop: "1px rgba(0, 0, 0, 0.05) solid",
                display: "flex",
                flexDirection: "column",
                gap: 10,
                boxSizing: "border-box",
                left: "50%",
                transform: "translateX(-50%)",
              }}
            >
              <button
                style={{
                  height: 48,
                  background: isAllFilled ? "#F57C00" : "#FFD9B3",
                  borderRadius: 8,
                  border: "none",
                  cursor: isAllFilled ? "pointer" : "",
                  fontSize: 16,
                  fontWeight: 500,
                  color: "white",
                }}
                disabled={!isAllFilled}
                onClick={handleSave}
              >
                추가하기
              </button>
            </div>
          )}
        </div>
      </>
    </PageContainer>
  );
};
